import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useMenuPermissions } from '../../hooks/useMenuPermissions';
import { useAuth } from '../../contexts/AuthContext';
import { useHelpdeskHomePath } from '../../hooks/useHelpdeskHomePath';

export const Sidebar: React.FC<{
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}> = ({ mobileOpen = false, onMobileClose }) => {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);
  const { getAllowedMenus, getUserRole, isAdmin } = useMenuPermissions();
  const { user } = useAuth();
  const { homePath: helpdeskHomePath } = useHelpdeskHomePath();

  const menuItems = getAllowedMenus();
  const userRole = getUserRole();
  const userIsAdmin = isAdmin();
  const canAccessAdmin = user?.role?.code === 'admin';
  const canAccessSupport =
    user?.isAdmin || userRole === 'admin' || userRole === 'manager';
  const hasModuleLinks = canAccessAdmin || canAccessSupport;

  const toggleSubmenu = (path: string) => {
    setExpandedMenus(prev =>
      prev.includes(path)
        ? prev.filter(p => p !== path)
        : [...prev, path]
    );
  };

  const isSubmenuExpanded = (path: string) => expandedMenus.includes(path);
  const isActive = (path: string) => {
    if (path === '/documents')
    {
      return (
        location.pathname.startsWith('/documents') ||
        location.pathname === '/sign/inbox' ||
        location.pathname.startsWith('/sign/inbox/') ||
        location.pathname === '/validate' ||
        location.pathname.startsWith('/validate/')
      );
    }

    return location.pathname.startsWith(path);
  };

  const isSubmenuLinkActive = (subPath: string) => {
    if (location.pathname === subPath)
    {
      return true;
    }

    if (subPath === '/sign/inbox' && location.pathname.startsWith('/sign/inbox/'))
    {
      return true;
    }

    if (subPath === '/validate' && location.pathname.startsWith('/validate/'))
    {
      return true;
    }

    return false;
  };

  const moduleLinkClass = (active: boolean) =>
    `block rounded-md px-3 py-2 text-sm font-medium ${
      active ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'
    }`;

  return (
    <>
      {mobileOpen && (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-black/40 md:hidden"
          aria-label="Fechar menu"
          onClick={onMobileClose}
        />
      )}
      <aside
        className={`bg-white shadow-md flex flex-col flex-shrink-0 z-50 h-full min-h-0 fixed inset-y-0 left-0 w-64 transition-transform duration-200 md:static md:z-40 ${
          collapsed ? 'md:w-20' : 'md:w-64'
        } ${mobileOpen ? 'translate-x-0' : '-translate-x-full pointer-events-none md:pointer-events-auto md:translate-x-0'}`}
      >
      <div className="flex flex-col h-full min-h-0">
        <div
          className={`border-b border-gray-200 flex items-center justify-between p-4 ${
            collapsed ? 'md:flex-col md:items-center md:gap-1 md:px-2 md:py-3' : ''
          }`}
        >
          <img src="/logo.png" alt="Logo" className="h-8" />
          <button
            className="md:hidden flex items-center justify-center p-2 rounded hover:bg-gray-100 focus:outline-none"
            onClick={onMobileClose}
            aria-label="Fechar menu"
            type="button"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <button
            className="hidden md:flex items-center justify-center p-2 rounded hover:bg-gray-100 focus:outline-none"
            onClick={() => {
              setCollapsed(c => !c);
            }}
            aria-label={collapsed ? 'Expandir menu' : 'Recolher menu'}
            type="button"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              {collapsed ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              )}
            </svg>
          </button>
        </div>

        {hasModuleLinks && (
          <div className="md:hidden px-3 py-3 border-b border-gray-200 space-y-1">
            {canAccessAdmin && (
              <>
                <Link
                  to="/admin/control-panel"
                  onClick={onMobileClose}
                  className={moduleLinkClass(location.pathname.startsWith('/admin'))}
                >
                  Administração
                </Link>
                <Link
                  to="/chatbot-admin/analytics"
                  onClick={onMobileClose}
                  className={moduleLinkClass(location.pathname.startsWith('/chatbot-admin'))}
                >
                  Chatbot
                </Link>
              </>
            )}
            {canAccessSupport && (
              <Link
                to={helpdeskHomePath}
                onClick={onMobileClose}
                className={moduleLinkClass(location.pathname.startsWith('/helpdesk'))}
              >
                Atendimento
              </Link>
            )}
          </div>
        )}

        {(!collapsed || mobileOpen) && (
          <div className={`px-4 py-2 border-b border-gray-200 ${collapsed ? 'md:hidden' : ''}`}>
            <div className="text-xs text-gray-500 uppercase tracking-wide">Função</div>
            <div className="text-sm font-medium text-gray-900">
              {userIsAdmin ? 'Administrador' :
               userRole === 'manager' ? 'Gerente' :
               userRole === 'editor' ? 'Editor' :
               userRole === 'viewer' ? 'Visualizador' :
               userRole === 'user' ? 'Signatário' :
               userRole || 'Usuário'}
            </div>
          </div>
        )}

        <nav className="flex-1 overflow-y-auto py-4">
          <ul>
            {menuItems.map(item => (
              <li key={item.path} className="mb-2">
                {item.submenu ? (
                  <div>
                    <button
                      type="button"
                      onClick={() => {
                        toggleSubmenu(item.path);
                      }}
                      className={`w-full flex items-center px-4 py-2 rounded-md transition-colors duration-150 ${
                        isActive(item.path)
                          ? 'bg-blue-50 text-blue-600 border-r-4 border-r-blue-600'
                          : 'text-gray-600 hover:bg-gray-50'
                      } ${collapsed ? 'md:justify-center' : ''}`}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className={`h-5 w-5 mr-3 ${collapsed ? 'md:mr-0' : ''} ${
                          isActive(item.path) ? 'text-blue-600' : 'text-gray-600'
                        }`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d={item.icon}
                        />
                      </svg>
                      <span className={`flex-1 text-left ${collapsed ? 'md:hidden' : ''}`}>{item.label}</span>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className={`h-4 w-4 transition-transform duration-200 ${collapsed ? 'md:hidden' : ''} ${
                          isSubmenuExpanded(item.path) ? 'rotate-180' : ''
                        }`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {(!collapsed || mobileOpen) && isSubmenuExpanded(item.path) && (
                      <ul className={`mt-1 ml-4 space-y-1 ${collapsed ? 'md:hidden' : ''}`}>
                        {item.submenu.map(subItem => (
                          <li key={subItem.path}>
                            <Link
                              to={subItem.path}
                              onClick={onMobileClose}
                              className={`flex items-center px-4 py-2 rounded-md transition-colors duration-150 ${
                                isSubmenuLinkActive(subItem.path)
                                  ? 'bg-blue-100 text-blue-700 border-r-4 border-r-blue-600'
                                  : 'text-gray-600 hover:bg-gray-50'
                              }`}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className={`h-4 w-4 mr-3 ${
                                  isSubmenuLinkActive(subItem.path) ? 'text-blue-700' : 'text-gray-600'
                                }`}
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d={subItem.icon}
                                />
                              </svg>
                              <span>{subItem.label}</span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ) : (
                  <Link
                    to={item.path}
                    onClick={onMobileClose}
                    className={`flex items-center px-4 py-2 rounded-md transition-colors duration-150 ${
                      location.pathname === item.path
                        ? 'bg-blue-50 text-blue-600 border-r-4 border-r-blue-600'
                        : 'text-gray-600 hover:bg-gray-50'
                    } ${collapsed ? 'md:justify-center' : ''}`}
                    tabIndex={0}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className={`h-5 w-5 mr-3 ${collapsed ? 'md:mr-0' : ''} ${
                        location.pathname === item.path ? 'text-blue-600' : 'text-gray-600'
                      }`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d={item.icon}
                      />
                    </svg>
                    <span className={`transition-all duration-200 ${collapsed ? 'md:hidden' : ''}`}>{item.label}</span>
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </nav>
      </div>
      </aside>
    </>
  );
};
